import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { pricingApiService } from '@/services/pricingApi'
import { getCountryConfig, COUNTRIES_CONFIG, UNSUPPORTED_COUNTRY_CODES } from '@/config/countries'
import { slugToCountryCode, getCountryName, countryToSlug } from '@/lib/countries'
import { useCurrency } from '@/hooks/useCurrency'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Phone } from 'lucide-react'
import { CountryRate } from '@/types/pricing'
import { useLang } from '@/hooks/useLang'
import { NotFoundContent } from '@/components/NotFoundContent'
import { getCountryImageAttribution } from '@/lib/country-images'
import { ResponsiveImage } from '@responsive-image/react'
import type { ImageData } from '@responsive-image/core'
import { generateSeo, SITE_URL } from '@/lib/seo'
import { useTranslation } from 'react-i18next'
import { Flag } from '@/components/Flag'
import { CTASection } from '@/components/CTASection'
import { getCurrencyConfig, DEFAULT_CURRENCY } from '@/config/currency'

const countryImages = import.meta.glob<ImageData>(
  '/src/assets/images/countries/*.jpg',
  { query: '?responsive', import: 'default', eager: true }
)

function getCountryImage(countryCode: string): ImageData | undefined {
  return countryImages[`/src/assets/images/countries/${countryCode.toLowerCase()}.jpg`]
}

const AMOUNTS = [1, 5, 10, 20]

export const Route = createFileRoute('/$lang/rates/$country')({
  staticData: {
    sitemap: {
      priority: '0.7',
      changefreq: 'weekly',
      entries: Object.keys(COUNTRIES_CONFIG)
        .filter((code) => !UNSUPPORTED_COUNTRY_CODES.includes(code))
        .map((code) => ({ path: `/rates/${countryToSlug(code)}` })),
    },
  },
  loader: async ({ params }) => {
    const isoCode = slugToCountryCode(params.country)
    if (!isoCode) throw notFound()

    const config = getCountryConfig(isoCode)
    if (!config) throw notFound()

    // Handle API failure gracefully - show country page without pricing
    let countryRate: CountryRate | undefined
    let related: CountryRate[] = []

    try {
      const data = await pricingApiService.getPricing()
      countryRate = data.countries.find(c => c.countryCode === isoCode)

      related = countryRate
        ? data.countries
            .filter(c => {
              const cfg = getCountryConfig(c.countryCode)
              return c.countryCode !== isoCode && cfg?.region === config.region
            })
            .slice(0, 6)
        : []
    } catch (error) {
      console.error('Failed to fetch pricing data:', error)
      // Continue without pricing data - component will show "not supported" view
    }

    const imageAttribution = getCountryImageAttribution(isoCode)

    return { countryRate: countryRate ?? null, related, imageAttribution, countryConfig: config }
  },
  head: ({ loaderData, match }) => {
    if (!loaderData) {
      return { meta: [], links: [] }
    }
    const { countryRate, countryConfig } = loaderData

    const countryName = getCountryName(countryConfig.code)

    // Handle case where country exists but pricing is not available
    if (!countryRate) {
      const { meta, links } = generateSeo({
        title: `Calls to ${countryName} from the UK | PhoneSalad`,
        description: `Information about calling ${countryName} from the UK. Country code ${countryConfig.dialingCode}. PhoneSalad offers affordable international calls.`,
        keywords: `call ${countryName} from UK, ${countryName} phone rates, international calls ${countryName}`,
        pathname: match.pathname,
      })
      return { meta, links }
    }

    const priceCurrency = countryRate.currency ?? DEFAULT_CURRENCY.code
    const currencySymbol = getCurrencyConfig(priceCurrency).symbol
    const landlineRate = countryRate.landlineRate.toFixed(4).replace(/\.?0+$/, '')
    const mobileRate = countryRate.mobileRate.toFixed(4).replace(/\.?0+$/, '')

    const { meta, links } = generateSeo({
      title: `Cheap Calls to ${countryName} from the UK | PhoneSalad`,
      description: `Call ${countryName} from ${currencySymbol}${landlineRate}/min to landline and ${currencySymbol}${mobileRate}/min to mobile. No contract, no hidden fees. Pay as you go international calls.`,
      keywords: `cheap calls to ${countryName}, call ${countryName} from UK, ${countryName} phone rates, international calls ${countryName}`,
      pathname: match.pathname,
    })

    const faqs = buildFaqs(countryRate, landlineRate, mobileRate, countryName, currencySymbol)

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(q => ({
        '@type': 'Question',
        name: q.q,
        acceptedAnswer: { '@type': 'Answer', text: q.a },
      })),
    }

    const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `International calls to ${countryName}`,
      description: `Make affordable calls to ${countryName} from the UK`,
      image: `${SITE_URL}/og.png`,
      brand: {
        '@type': 'Brand',
        name: 'PhoneSalad',
      },
      offers: [
        {
          '@type': 'Offer',
          name: 'Landline',
          price: landlineRate,
          priceCurrency: priceCurrency,
          availability: 'https://schema.org/InStock',
          priceValidUntil,
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: landlineRate,
            priceCurrency: priceCurrency,
            unitText: 'MIN',
          },
        },
        {
          '@type': 'Offer',
          name: 'Mobile',
          price: mobileRate,
          priceCurrency: priceCurrency,
          availability: 'https://schema.org/InStock',
          priceValidUntil,
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: mobileRate,
            priceCurrency: priceCurrency,
            unitText: 'MIN',
          },
        },
      ],
    }

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(faqSchema),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(productSchema),
        },
      ],
    }
  },
  component: CountryRatePage,
  notFoundComponent: NotFoundContent,
})

function buildFaqs(country: CountryRate, landlineRate: string, mobileRate: string, name: string, currencySymbol = DEFAULT_CURRENCY.symbol) {
  const dialCode = country.dialingCode

  const landlineMins5 = Math.floor(5 / parseFloat(landlineRate))
  const mobileMins5 = Math.floor(5 / parseFloat(mobileRate))

  return [
    {
      q: `How do I call ${name} from the UK?`,
      a: `Dial ${dialCode} (the ${name} country code), then the local number without the leading zero. For example, if the local number is 020XXXXXXX, dial ${dialCode} 20XXXXXXX. With PhoneSalad you can call directly from your browser — no app download required.`,
    },
    {
      q: `What is the country code for ${name}?`,
      a: `The country code for ${name} is ${dialCode}. Dial this before the local number when calling from the UK or anywhere internationally.`,
    },
    {
      q: `Is it cheaper to call ${name} landlines or mobiles?`,
      a: `Landline calls to ${name} cost ${currencySymbol}${landlineRate}/min and mobile calls cost ${currencySymbol}${mobileRate}/min. ${parseFloat(landlineRate) < parseFloat(mobileRate) ? `Landlines are cheaper, so if possible use the landline number.` : parseFloat(landlineRate) === parseFloat(mobileRate) ? `Landline and mobile rates are the same for ${name}.` : `Mobiles are cheaper in this case.`}`,
    },
    {
      q: `How many minutes can I call ${name} for ${currencySymbol}5?`,
      a: `With PhoneSalad, ${currencySymbol}5 gets you approximately ${landlineMins5} minutes to a ${name} landline or ${mobileMins5} minutes to a ${name} mobile. Calls are billed per minute with no connection fees.`,
    },
    {
      q: `Are there any connection fees for calling ${name}?`,
      a: `No. PhoneSalad charges no connection fees, no monthly subscription, and no hidden charges. You only pay for the minutes you use at the advertised per-minute rate.`,
    },
    {
      q: `Do credits expire when calling ${name}?`,
      a: `No, your PhoneSalad credits never expire. Top up whenever you like and use them at your own pace — there's no pressure to use them within a certain period.`,
    },
    {
      q: `What quality can I expect when calling ${name}?`,
      a: `PhoneSalad uses VoIP technology routed through Twilio's global network to deliver crystal-clear call quality to ${name}. Call quality depends on your internet connection speed (broadband or 4G/5G recommended).`,
    },
    {
      q: `Can I call ${name} from my mobile or tablet?`,
      a: `Yes. PhoneSalad works in any modern web browser on desktop, mobile, or tablet. No app download is required — just sign up, top up, and call ${name} from anywhere.`,
    },
  ]
}

function buildFaqsUnsupported(countryConfig: NonNullable<ReturnType<typeof getCountryConfig>>, name: string) {
  const dialCode = countryConfig.dialingCode

  return [
    {
      q: `How do I call ${name} from the UK?`,
      a: `Dial ${dialCode} (the ${name} country code), then the local number without the leading zero. For example, if the local number is 020XXXXXXX, dial ${dialCode} 20XXXXXXX.`,
    },
    {
      q: `What is the country code for ${name}?`,
      a: `The country code for ${name} is ${dialCode}. Dial this before the local number when calling from the UK or anywhere internationally.`,
    },
    {
      q: `Is calling ${name} supported by PhoneSalad?`,
      a: `We are currently working on adding support for calls to ${name}. Check back soon for updated rates and availability.`,
    },
    {
      q: `When will calling ${name} be available?`,
      a: `We are actively expanding our coverage. Please check our pricing page or sign up for notifications to be informed when ${name} calling becomes available.`,
    },
    {
      q: `Are there any connection fees for calling ${name}?`,
      a: `When ${name} becomes available, PhoneSalad will charge no connection fees, no monthly subscription, and no hidden charges. You will only pay for the minutes you use at the advertised per-minute rate.`,
    },
    {
      q: `What quality can I expect when calling ${name}?`,
      a: `PhoneSalad uses VoIP technology routed through Twilio's global network to deliver crystal-clear call quality. Call quality depends on your internet connection speed (broadband or 4G/5G recommended).`,
    },
    {
      q: `Can I call ${name} from my mobile or tablet?`,
      a: `Yes, once ${name} is supported. PhoneSalad works in any modern web browser on desktop, mobile, or tablet. No app download is required — just sign up, top up, and call from anywhere.`,
    },
  ]
}

function CountryRatePage() {
  const { countryRate, related, imageAttribution, countryConfig } = Route.useLoaderData()
  const { formatCurrency, currency } = useCurrency()
  const lang = useLang()
  const { t } = useTranslation(['marketing', 'common', 'tools'])

  const hasPricing = !!countryRate
  const name = t(`countries.${countryConfig.code}`)

  const landlineRate = hasPricing ? countryRate.landlineRate.toFixed(4).replace(/\.?0+$/, '') : '0'
  const mobileRate = hasPricing ? countryRate.mobileRate.toFixed(4).replace(/\.?0+$/, '') : '0'
  const currencySymbol = currency.symbol

  const faqs = hasPricing ? buildFaqs(countryRate, landlineRate, mobileRate, name, currencySymbol) : buildFaqsUnsupported(countryConfig, name)
  const imageData = getCountryImage(countryConfig.code)
  const hasImage = !!imageData

  return (
    <>
      {/* Hero */}
      <section className={`relative min-h-[480px] flex items-center py-20 px-4 overflow-hidden ${hasImage ? 'text-white' : 'bg-gradient-to-b from-background to-muted/30'}`}>
        {imageData && (
          <>
            <div className="absolute inset-0" key={countryConfig.code}>
              <ResponsiveImage
                src={imageData}
                alt=""
                aria-hidden
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
          </>
        )}

        <div className="relative z-10 container mx-auto max-w-4xl">
          <div className={`flex items-center gap-3 mb-4 text-sm ${hasImage ? 'text-white/80' : 'text-muted-foreground'}`}>
            <Link to="/$lang/pricing" params={{ lang }} className="hover:text-white transition-colors">{t('navigation.allRates')}</Link>
            <span>/</span>
            <span>{name}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <Flag countryCode={countryConfig.code} size="xl" className="w-24 h-16" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-3 drop-shadow-lg">
                {hasPricing
                  ? t('pricing.countryRate.cheapCallsTo', { country: name })
                  : t('pricing.countryRate.callsToCountry', { country: name })}
              </h1>
              <p className={`text-xl mb-6 ${hasImage ? 'text-white/90' : 'text-muted-foreground'}`}>
                {hasPricing
                  ? t('pricing.countryRate.heroSubtitle', { rate: formatCurrency(countryRate.landlineRate, { useIntl: true }) })
                  : t('pricing.countryRate.notSupportedSubtitle')}
              </p>
              {hasPricing ? (
                <Button size="lg" asChild className={hasImage ? 'bg-white text-black hover:bg-white/90' : ''}>
                  <a href={`/dialer?phone=${encodeURIComponent(countryRate.dialingCode)}`}>
                    <Phone className="mr-2 h-5 w-5" />
                    {t('pricing.countryRate.callNow', { country: name })}
                  </a>
                </Button>
              ) : (
                <Button size="lg" asChild className={hasImage ? 'bg-white text-black hover:bg-white/90' : ''}>
                  <Link to="/$lang/pricing" params={{ lang }}>
                    {t('pricing.countryRate.viewSupportedCountries')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Attribution — pinned to bottom-right */}
        {hasImage && imageAttribution && (
          <p className="absolute bottom-2 right-3 z-10 text-[10px] text-white/30 hover:text-white/60 transition-colors">
            {t('common.photo')}:{' '}
            <a href={imageAttribution.unsplashUrl} target="_blank" rel="noopener noreferrer">
              {imageAttribution.photographer}
            </a>
            {' '}/ Unsplash
          </p>
        )}
      </section>

      {/* Rates */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          {hasPricing ? (
            <>
              <h2 className="text-2xl font-bold mb-6">{t('pricing.countryRate.currentRates', { country: name })}</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <Card>
                  <CardContent className="p-6">
                    <div className="text-sm text-muted-foreground mb-1">{t('pricing.countryRate.landline')}</div>
                    <div className="text-4xl font-bold text-green-600 mb-1">
                      {formatCurrency(countryRate.landlineRate, { useIntl: true })}
                      <span className="text-lg font-normal text-muted-foreground">/min</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{t('pricing.countryRate.dialInstructions', { code: countryRate.dialingCode })}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-sm text-muted-foreground mb-1">{t('pricing.countryRate.mobile')}</div>
                    <div className="text-4xl font-bold text-green-600 mb-1">
                      {formatCurrency(countryRate.mobileRate, { useIntl: true })}
                      <span className="text-lg font-normal text-muted-foreground">/min</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{t('pricing.countryRate.dialInstructions', { code: countryRate.dialingCode })}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Value Calculator */}
              <h2 className="text-2xl font-bold mb-4">{t('pricing.countryRate.valueCalculator', { country: name })}</h2>
              <div className="overflow-x-auto rounded-lg border mb-12">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-3 font-medium">{t('pricing.countryRate.credit')}</th>
                      <th className="text-left px-4 py-3 font-medium">{t('pricing.countryRate.landlineMinutes')}</th>
                      <th className="text-left px-4 py-3 font-medium">{t('pricing.countryRate.mobileMinutes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {AMOUNTS.map(amount => {
                      const landlineMins = Math.floor(amount / countryRate.landlineRate)
                      const mobileMins = Math.floor(amount / countryRate.mobileRate)
                      return (
                        <tr key={amount} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="px-4 py-3 font-semibold">{formatCurrency(amount, { useIntl: true })}</td>
                          <td className="px-4 py-3 text-green-600 font-mono">{t('pricing.countryRate.mins', { mins: landlineMins })}</td>
                          <td className="px-4 py-3 text-green-600 font-mono">{t('pricing.countryRate.mins', { mins: mobileMins })}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* How to call */}
              <h2 className="text-2xl font-bold mb-4">{t('pricing.countryRate.howToCall', { country: name })}</h2>
              <ol className="space-y-3 mb-12 text-muted-foreground">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">1</span>
                  <span>{t('pricing.countryRate.step1')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">2</span>
                  <span>{t('pricing.countryRate.step2', { code: countryRate.dialingCode, country: name })}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">3</span>
                  <span>{t('pricing.countryRate.step3')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">4</span>
                  <span>{t('pricing.countryRate.step4', { rate: formatCurrency(countryRate.landlineRate, { useIntl: true }) })}</span>
                </li>
              </ol>
            </>
          ) : (
            <>
              {/* Not Supported Message */}
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="text-4xl">🔜</div>
                  <div>
                    <h2 className="text-2xl font-bold mb-3">{t('pricing.countryRate.notSupportedTitle', { country: name })}</h2>
                    <p className="text-muted-foreground mb-4">
                      {t('pricing.countryRate.notSupportedDescription', { country: name })}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button asChild>
                        <Link to="/$lang/pricing" params={{ lang }}>
                          {t('pricing.countryRate.viewSupportedCountries')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Country Info Card */}
              <Card className="mb-12">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{t('pricing.countryRate.countryInfo', { country: name })}</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">{t('tools.countryCodes.columns.dialingCode')}</div>
                      <div className="text-2xl font-mono font-semibold">{countryConfig.dialingCode}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">{t('tools.countryCodes.columns.isoCode')}</div>
                      <div className="text-2xl font-mono font-semibold">{countryConfig.code}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">{t('pricing.region')}</div>
                      <div className="text-lg font-semibold">{countryConfig.region}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">{t('pricing.countryRate.landline')}</div>
                      <div className="text-lg font-semibold text-muted-foreground">{t('pricing.countryRate.comingSoon')}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* How to call (simplified for unsupported) */}
              <h2 className="text-2xl font-bold mb-4">{t('pricing.countryRate.howToCall', { country: name })}</h2>
              <ol className="space-y-3 mb-12 text-muted-foreground">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">1</span>
                  <span>{t('pricing.countryRate.step1')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">2</span>
                  <span>{t('pricing.countryRate.step2', { code: countryConfig.dialingCode, country: name })}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">3</span>
                  <span>{t('pricing.countryRate.step3')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">4</span>
                  <span>{t('pricing.countryRate.step4Unsupported')}</span>
                </li>
              </ol>
            </>
          )}

          {/* FAQ */}
          <h2 className="text-2xl font-bold mb-6">{t('faq.title')}</h2>
          <div className="space-y-2 mb-12">
            {faqs.map((faq, i) => (
              <details key={i} className="group border rounded-lg">
                <summary className="flex items-center justify-between px-4 py-3 cursor-pointer font-medium list-none">
                  {faq.q}
                  <span className="ml-4 flex-shrink-0 text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <div className="px-4 pb-4 text-muted-foreground text-sm leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>

          {/* CTA */}
          <div className="mb-12">
            {hasPricing ? (
              <CTASection
                asSection={false}
                title={t('pricing.countryRate.readyToCall', { country: name })}
                description={t('pricing.countryRate.ctaDescription', { country: name, rate: formatCurrency(countryRate.landlineRate, { useIntl: true }) })}
                buttonTo="/dialer"
                buttonLabel={t('navigation.getStarted')}
                secondaryButton={
                  <Button size="lg" variant="outline" asChild>
                    <a href={`/dialer?phone=${encodeURIComponent(countryRate.dialingCode)}`}>
                      <Phone className="mr-2 h-4 w-4" />
                      {t('pricing.countryRate.callNow', { country: name })}
                    </a>
                  </Button>
                }
              />
            ) : (
              <CTASection
                asSection={false}
                title={t('pricing.countryRate.exploreOtherDestinations')}
                description={t('pricing.countryRate.exploreDescription', { country: name })}
                buttonTo="/$lang/pricing"
                buttonParams={{ lang }}
                buttonLabel={t('navigation.pricing')}
              />
            )}
          </div>

          {/* Related countries */}
          {related.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-4">{t('pricing.countryRate.otherDestinations', { region: countryRate?.region || countryConfig.region })}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {related.map(r => {
                  const cfg = getCountryConfig(r.countryCode)
                  if (!cfg) return null
                  return (
                    <Link
                      key={r.countryCode}
                      to="/$lang/rates/$country"
                      params={{ lang, country: countryToSlug(r.countryCode) }}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <Flag countryCode={r.countryCode} size="lg" />
                      <div>
                        <div className="font-medium text-sm">{t(`countries.${r.countryCode}`)}</div>
                        <div className="text-xs text-green-600">{t('pricing.rateWithUnit', { rate: formatCurrency(r.landlineRate, { useIntl: true }) })}</div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  )
}
